/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import ExportProcess, {ExportProcessFields} from "../../../models/exportProcessModel";
import {AppContext} from "../../misc/appContext";
import {createErrorMessage} from "../../../util/utils";
import {
    ActivateHandle,
    CustomerType,
    ExportNameAttr,
    ExportProcessStatus, Fulfilment,
    InvoiceExportType
} from "../../../util/interfaces";
import ExportInvoices from "./exportInvoices";
import {onSetCrumbs} from "../../../util/events";
import {defaultInvoiceFields, defaultOrderFields} from "../../../util/exportHelper";
import ExportFieldsSelector from "./exportFieldsSelector";

let loadTimeout: number | null = null;
const TIMEOUT = 1000;
export default function ExportInvoicesController() {
    const {accountContext} = useContext(AppContext);
    const [exportProcess, setProcess] = useState(new ExportProcess({account: accountContext.account}));
    const [exportProcesses, setProcesses] = useState(new Array<ExportProcess>());
    const [loadingCount, setLoadingCount] = useState(false);
    const [loadingProcesses, setLoadingProcesses] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [invoiceCount, setInvoiceCount] = useState(0);
    const [country, setCountry] = useState('');
    const [fulfilment, setFulfilment] = useState(Fulfilment.BOTH);
    const [nameAttr, setNameAttr] = useState(ExportNameAttr.ORDER_ID);
    const [reverseChargeOnly, setReverseChargeOnly] = useState(false);
    const [customerType, setCustomerType] = useState(CustomerType.BOTH);
    const [selectedInvoiceFields, setInvoiceFields] = useState<Array<string>>(defaultInvoiceFields);
    const [selectedOrderFields, setOrderFields] = useState<Array<string>>(defaultOrderFields);
    const fieldsRef = useRef<ActivateHandle>(null);

    function onClickFields() {
        fieldsRef.current?.activate(selectedInvoiceFields, selectedOrderFields);
    }

    function onSubmitFields(invoiceFields: Array<string>, orderFields: Array<string>) {
        setInvoiceFields(invoiceFields);
        setOrderFields(orderFields);
    }

    const onChange = (key: keyof ExportProcessFields) => (value: any) => {
        const process = exportProcess.clone();
        process.setField(key, value);
        setProcess(process);
    };

    function onChangeReverseCharge() {
        setReverseChargeOnly(!reverseChargeOnly);
    }

    function onChangeCountry(value: string) {
        setCountry(value);
    }

    function onChangeFulfilment(value: Fulfilment) {
        setFulfilment(value);
    }

    function onChangeNameAttr(value: ExportNameAttr) {
        setNameAttr(value);
    }

    function onChangeCustomerType(customerType: CustomerType) {
        setCustomerType(customerType);
    }

    const onDownloadProcess = (process: ExportProcess) => () => {
        if (process.status === ExportProcessStatus.FINISHED) {
            setLoadingProcesses(true);
            process.download(() => {
                setLoadingProcesses(false);
            }, (data: ResponseData) => {
                setLoadingProcesses(false);
                showDialog(data.message ?? createErrorMessage('Het downloaden van het bestand is niet gelukt.'),
                    'Downloaden mislukt');
            })
        }
    };

    function onSubmitProcess() {
        if (!validateValues()) return;
        setLoadingSubmit(true);
        exportProcess.export((exportProcess: ExportProcess, success: boolean, data: ResponseData) => {
            setLoadingSubmit(false);
            if (data.success) {
                showSnackbar('De export wordt aangemaakt');
                loadProcesses();
                window.scroll({top: document.body.scrollHeight, behavior: 'smooth'});
            } else showDialog(data.message ?? createErrorMessage('Het aanmaken van de export is niet gelukt.'),
                'Aanmaken mislukt');
        }, {
            country, reverseChargeOnly, customerType, invoice_field_names: selectedInvoiceFields,
            order_field_names: selectedOrderFields, fulfilment
        }, nameAttr);
    }

    function getInvoiceCount() {
        if (!exportProcess.fromDate || !exportProcess.toDate) return;
        setLoadingCount(true);
        exportProcess.getCount((process: ExportProcess, success: boolean, data: ResponseData) => {
            setLoadingCount(false);
            if (data.success) setInvoiceCount(data.invoice_count);
            else showDialog(data.message ?? createErrorMessage('Het laden van het aantal facturen is niet gelukt.'),
                'Laden mislukt');
        }, {country, reverseChargeOnly, customerType, fulfilment});
    }

    function validateValues(): boolean {
        const {toDate, fromDate, toDateMoment, fromDateMoment} = exportProcess;
        let error: string | null = null;
        if (!toDate) error = 'Kies de datum vanaf wanneer de facturen geëxporteerd moeten worden.';
        else if (!fromDate) error = 'Kies de datum tot en met wanneer de facturen geëxporteerd moeten worden.';
        else if (toDateMoment!.isBefore(fromDateMoment!)) error =
            'De Tot en met datum kan niet voor de Vanaf datum zijn.';
        if (error) showDialog(error, 'Fout');
        return error === null;
    }

    function loadProcesses(showLoad: boolean = true) {
        clearTimeout();
        if (accountContext.account) {
            if (!loadingProcesses && showLoad) setLoadingProcesses(true);
            const data = {
                account_id: accountContext.account.id,
                export_types: [InvoiceExportType.CSV, InvoiceExportType.PDF, InvoiceExportType.UBL,
                    InvoiceExportType.COMBINED_PDF, InvoiceExportType.CSV_CUSTOM]
            };
            ExportProcess.getAll(data,
                (processes: ExportProcess[], success: boolean, data: ResponseData) => {
                    setLoadingProcesses(false);
                    if (success) {
                        setProcesses(processes);
                        let hasActiveExport = processes.filter(
                            (proc: ExportProcess) => proc.status === ExportProcessStatus.EXPORTING).length > 0;
                        if (hasActiveExport)
                            loadTimeout = window.setTimeout(() => {
                                loadProcesses(false);
                            }, TIMEOUT);
                    } else showDialog(data.message ?? createErrorMessage('Het laden van de exports is niet gelukt.'),
                        'Laden mislukt');
                })
        }
    }

    function clearTimeout() {
        if (loadTimeout !== null) {
            window.clearTimeout(loadTimeout);
            loadTimeout = null;
        }
    }

    useEffect(() => {
        onSetCrumbs([{label: 'Instellingen'}, {label: 'Exporteren'}]);
        return () => {
            clearTimeout()
        };
    }, []);

    useEffect(() => {
        getInvoiceCount();
    }, [exportProcess, country, reverseChargeOnly, customerType, fulfilment]);

    useEffect(() => {
        loadProcesses();
    }, [accountContext.account]);

    return (
        <>
            <ExportInvoices onChange={onChange} onDownloadProcess={onDownloadProcess} onSubmitProcess={onSubmitProcess}
                            onChangeCountry={onChangeCountry} onChangeNameAttr={onChangeNameAttr}
                            onChangeReverseCharge={onChangeReverseCharge} onChangeCustomerType={onChangeCustomerType}
                            onClickFields={onClickFields} onChangeFulfilment={onChangeFulfilment}
                            exportProcess={exportProcess} nameAttr={nameAttr} exportProcesses={exportProcesses}
                            loadingCount={loadingCount} loadingProcesses={loadingProcesses}
                            loadingSubmit={loadingSubmit} invoiceCount={invoiceCount} country={country}
                            reverseChargeOnly={reverseChargeOnly} customerType={customerType} fulfilment={fulfilment}
                            selectedFieldsCount={selectedInvoiceFields.length + selectedOrderFields.length}/>
            <ExportFieldsSelector onSubmitFields={onSubmitFields} ref={fieldsRef}/>
        </>
    );
}